import { lazy, FC, Suspense, useCallback } from 'react'
import { Route, Routes, Navigate, useNavigate } from 'react-router-dom'
import { MasterLayout } from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import { DashboardWrapper } from '../pages/dashboard/DashboardWrapper'
import { MenuTestPage } from '../pages/MenuTestPage'
import { getCSSVariableValue } from '../../_metronic/assets/ts/_utils'
import { WithChildren } from '../../_metronic/helpers'
import BuilderPageWrapper from '../pages/layout-builder/BuilderPageWrapper'
import BulkReport from '../pages/BulkReport/BulkReport'
import HopsManagement from '../pages/HopsManagement/HopsManagement'
import { useAuth } from '../modules/auth'
import useInactivityTimeout from '../hooks/useInactivityTimeout'
import HopsApproval from '../pages/HopsApproval/HopsApproval'
import FAQ from '../pages/faq/FAQ'
import { ReportingSection } from '../pages/reporting/ReportingSection'
import { SupportCenterWrapper } from '../pages/supportCenter/Support'
import { featureFlags } from '../../config/countryConfig'

const PrivateRoutes = () => {
  const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'))
  const WizardsPage = lazy(() => import('../modules/wizards/WizardsPage'))
  const AccountPage = lazy(() => import('../modules/accounts/AccountPage'))
  const WidgetsPage = lazy(() => import('../modules/widgets/WidgetsPage'))
  const ChatPage = lazy(() => import('../modules/apps/chat/ChatPage'))
  const UsersPage = lazy(() => import('../modules/apps/user-management/UsersPage'))

  const { auth, logout } = useAuth();
  const isKybFlow = featureFlags.onboarding && auth?.flow === 'kyb'

  const nav = useNavigate()
  const handleInactivityLogout = useCallback(async () => {
    if (auth?.access_token) {
      await logout()
      nav('/auth')
    }
  }, [auth?.access_token, logout, nav])
  useInactivityTimeout(handleInactivityLogout,60 * 60 * 1000)

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registartion */}
        <Route path='auth/*' element={<Navigate to='/dashboard' />} />
        {/* Pages */}
        <Route path='dashboard' element={<DashboardWrapper />} />
        {!isKybFlow && (
          <>
            <Route path='bulkreport' element={<BulkReport />} />
            <Route path='hops' element={<HopsManagement />} />
            <Route path='approve-hops' element={<HopsApproval />} />
            <Route path='builder' element={<BuilderPageWrapper />} />
            <Route path='menu-test' element={<MenuTestPage />} />
            <Route path='support' element={<SupportCenterWrapper />} />
            <Route path='report-section' element={<ReportingSection />} />
            {/* Lazy Modules */}
            <Route
              path='profile/*'
              element={
                <SuspensedView>
                  <ProfilePage />
                </SuspensedView>
              }
            />
            <Route
              path='crafted/pages/wizards/*'
              element={
                <SuspensedView>
                  <WizardsPage />
                </SuspensedView>
              }
            />
            <Route
              path='crafted/widgets/*'
              element={
                <SuspensedView>
                  <WidgetsPage />
                </SuspensedView>
              }
            />
            <Route
              path='crafted/account/*'
              element={
                <SuspensedView>
                  <AccountPage />
                </SuspensedView>
              }
            />
            <Route
              path='apps/chat/*'
              element={
                <SuspensedView>
                  <ChatPage />
                </SuspensedView>
              }
            />
            <Route
              path='apps/user-management/*'
              element={
                <SuspensedView>
                  <UsersPage />
                </SuspensedView>
              }
            />
          </>
        )}
        {/* Page Not Found / KYB flow catch-all */}
        <Route path='*' element={<Navigate to={isKybFlow ? '/dashboard' : '/error/404'} replace />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export { PrivateRoutes }
